import type { ReactNode } from 'react';
import { CheckCircle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface ChoiceTileProps {
  /** Omitted for text-only choices ("7 days", "Until lifted"). */
  icon?: PhosphorIcon;
  label: string;
  /** One short line under the label ("Coming soon"). */
  hint?: ReactNode;
  selected?: boolean;
  /** A check beside the icon when chosen. The domain picker (Figma A05.1b) shows the chosen tile by its 2px stroke alone. */
  showCheck?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  className?: string;
}

/** Square choice tile (Figma A05.1b "Domain", A05.3 "What kind of task?"): icon, label, a check when chosen. */
export function ChoiceTile({
  icon,
  label,
  hint,
  selected = false,
  showCheck = true,
  disabled = false,
  onSelect,
  className,
}: ChoiceTileProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cx(
        'flex flex-col items-start gap-(--mbm-space-2) rounded-(--mbm-component-radius-input) bg-surface-card p-(--mbm-space-3) text-left',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        'disabled:cursor-not-allowed disabled:opacity-(--mbm-opacity-disabled)',
        selected ? 'border-2 border-action-primary' : 'border border-border-default',
        className,
      )}
    >
      <span className="flex w-full items-center justify-between text-text-primary">
        {icon ? <Icon icon={icon} size="md" /> : <span />}
        {selected && showCheck ? <Icon icon={CheckCircle} size="md" /> : null}
      </span>
      <span className="flex flex-col">
        <span className="text-label-medium text-text-primary">{label}</span>
        {hint ? <span className="text-body-xsmall text-text-tertiary">{hint}</span> : null}
      </span>
    </button>
  );
}
